import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAccountStorage } from './account/context';
import type { KeyValueStorage } from './account/storage';

/**
 * Events the user marked "Not Interested", kept off Explore.
 *
 * localStorage for now. Once accounts are live this list belongs to the account,
 * so an event hidden on one device stays hidden on every device the user signs
 * in on. See handoff/hidden-and-edited-events.md.
 */

const KEY = 'mcw.hidden';

function read(storage: KeyValueStorage): string[] {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export interface HiddenEvents {
  ids: Set<string>;
  isHidden: (id: string) => boolean;
  hide: (id: string) => void;
  unhide: (id: string) => void;
}

export function useHiddenEvents(): HiddenEvents {
  /** localStorage, or the account once the user has one. */
  const storage = useAccountStorage();
  const [list, setList] = useState<string[]>(() => read(storage));

  // The list moved: start again from wherever it now lives.
  const [source, setSource] = useState(storage);
  if (source !== storage) {
    setSource(storage);
    setList(read(storage));
  }

  useEffect(() => {
    try {
      storage.setItem(KEY, JSON.stringify(list));
    } catch {
      // Site data blocked. Hiding still works for this session.
    }
  }, [list, storage]);

  const ids = useMemo(() => new Set(list), [list]);
  const hide = useCallback((id: string) => setList((l) => (l.includes(id) ? l : [...l, id])), []);
  const unhide = useCallback((id: string) => setList((l) => l.filter((x) => x !== id)), []);

  return useMemo(
    () => ({ ids, isHidden: (id: string) => ids.has(id), hide, unhide }),
    [ids, hide, unhide],
  );
}
